import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

/**
 * @param {{
 *   open: boolean,
 *   drawingTitle?: string,
 *   loading?: boolean,
 *   error?: string | null,
 *   onClose: () => void,
 *   onConfirm: () => void,
 * }} props
 */
export default function DeletePatrolDrawingConfirmDialog({
  open,
  drawingTitle = "",
  loading = false,
  error = null,
  onClose,
  onConfirm,
}) {
  const label = drawingTitle.trim() || "this drawing";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: 3,
          fontSize: 18,
          fontWeight: 600,
          color: "#101828",
        }}
      >
        Confirm Delete
        <IconButton
          size="small"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mb: 2,
          }}
        >
          <WarningAmberOutlinedIcon
            sx={{ fontSize: 20, color: "#DC2626", mt: 0.25, flexShrink: 0 }}
          />
          <Typography sx={{ fontSize: 14, color: "#364153", lineHeight: 1.5 }}>
            This action will delete{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              &quot;{label}&quot;
            </Box>
            .
          </Typography>
        </Box>
        {error ? (
          <Typography sx={{ fontSize: 13, color: "#DC2626", mb: 1.5 }}>
            {error}
          </Typography>
        ) : null}
        <Typography sx={{ fontSize: 14, color: "#4A5565" }}>
          Are you sure you want to delete this record?
        </Typography>
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 3,
          py: 2,
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            color: "#101828",
            bgcolor: "#fff",
            px: 2,
            boxShadow: "none",
            "&:hover": { bgcolor: "#F9FAFB", boxShadow: "none" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            bgcolor: "#DC2626",
            color: "#fff",
            px: 2,
            minWidth: 88,
            boxShadow: "none",
            "&:hover": { bgcolor: "#B91C1C", boxShadow: "none" },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Delete"
          )}
        </Button>
      </Box>
    </Dialog>
  );
}
